import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AllergenStatus } from '../types';
import { ProductAllergenEntry, emptyAllergenRow } from '../types';
import { AllergenOption, AllergenMasterData, CodeNameOption } from '../../../../app/services/api/orderManagementClient';
import { ALLERGEN_STATUS_LABEL_KEYS, ALLERGEN_STATUS_MEANING_KEYS, ASSESSMENT_SOURCE_TYPE_LABEL_KEYS, getEnumLabel } from '../enumLabels';

interface AllergensTabProps {
  allergens: ProductAllergenEntry[];
  onChange: (allergens: ProductAllergenEntry[]) => void;
  disabled?: boolean;
  referenceOptions?: AllergenMasterData | null;
}

const toDateValue = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const AllergensTab: FC<AllergensTabProps> = ({ allergens, onChange, disabled, referenceOptions }) => {
  const { t } = useTranslation('productManagement');
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const updateRow = (index: number, patch: Partial<ProductAllergenEntry>) => {
    const next = allergens.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const removeRow = (index: number) => {
    // Row identity is positional (key={index}); reset expanded state so a delete
    // can't leave a later row's expanded panel misattributed to its new position.
    setExpandedRows({});
    onChange(allergens.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([...allergens, emptyAllergenRow()]);
  };

  const toggleExpanded = (index: number) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="textLG"
          component="div"
          sx={{ fontWeight: 600 }}>
          {t('allergens', 'Allergens')}
        </Typography>
        <Button startIcon={<AddIcon />}
          onClick={addRow}
          disabled={disabled}
          size="md">
          {t('addAllergen', 'Add allergen')}
        </Button>
      </Box>

      {allergens.length === 0 && (
        <Typography variant="textSM"
          component="div"
          color="text.secondary"
          sx={{ mb: 3 }}>
          {t('noAllergensCaptured', 'No allergens captured yet.')}
        </Typography>
      )}

      {allergens.map((row, index) => {
        const isExpanded = Boolean(expandedRows[index]);
        return (
          <Box key={index}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, pt: '12px', pr: 3, pb: 0, pl: 3, mb: 3 }}>
            <Grid container
              spacing={3}
              alignItems="center">
              <Grid item
                xs={12}
                sm={4}>
                {referenceOptions ? (
                  <Autocomplete<AllergenOption>
                    options={referenceOptions.allergens}
                    getOptionLabel={(option) => (option.variantCode ? `${option.code} - ${option.variantName ?? option.variantCode}` : option.code)}
                    isOptionEqualToValue={(option, value) => option.code === value.code && (option.variantCode ?? '') === (value.variantCode ?? '')}
                    value={referenceOptions.allergens.find((a) => a.code === row.allergenCode && (a.variantCode ?? '') === (row.allergenVariantCode ?? '')) ?? null}
                    onChange={(_e, newValue) => updateRow(index, {
                      allergenCode: newValue?.code ?? '',
                      allergenName: newValue?.name ?? '',
                      allergenVariantCode: newValue?.variantCode ?? '',
                      allergenVariantName: newValue?.variantName ?? '',
                    })}
                    disabled={disabled}
                    size="small"
                    renderOption={(props, option) => (
                      <li {...props} 
                        key={`${option.code}-${option.variantCode ?? ''}`}>{option.name ? `${option.code} - ${option.name}` : option.code}{option.variantCode ? ` (${option.variantName ?? option.variantCode})` : ''}</li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params}
                        label={t('allergenCode', 'Code')}
                        required
                      />
                    )}
                  />
                ) : (
                  <TextField
                    label={t('allergenCode', 'Code')}
                    value={row.allergenCode}
                    onChange={(e) => updateRow(index, { allergenCode: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                    required
                  />
                )}
              </Grid>
              <Grid item
                xs={12}
                sm={4}>
                <TextField
                  label={t('allergenName', 'Allergen Name')}
                  value={row.allergenName ?? ''}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item
                xs={8}
                sm={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FormControl fullWidth
                    size="small">
                    <InputLabel shrink>{t('status', 'Status')}</InputLabel>
                    <Select
                      value={row.allergenStatus ?? AllergenStatus.Unknown}
                      onChange={(e: SelectChangeEvent) => updateRow(index, { allergenStatus: e.target.value })}
                      size="small"
                      disabled={disabled}
                      fullWidth
                      label={t('status', 'Status')}
                      notched
                    >
                      {(referenceOptions?.allergenStatuses ?? Object.values(AllergenStatus)).map((status) => (
                        <MenuItem key={status}
                          value={status}>
                          {getEnumLabel(t, ALLERGEN_STATUS_LABEL_KEYS, status)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title={(
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5 }}>
                      {Object.values(AllergenStatus).map((status) => (
                        <Box key={status}
                          sx={{ display: 'contents' }}>
                          <Box sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {getEnumLabel(t, ALLERGEN_STATUS_LABEL_KEYS, status)}:
                          </Box>
                          <Box>{getEnumLabel(t, ALLERGEN_STATUS_MEANING_KEYS, status)}</Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                  placement="right"
                  slotProps={{ tooltip: { sx: { maxWidth: 420 } } }}
                  arrow>
                    <IconButton size="small"
                      sx={{ ml: 1, mb: 10, p: 0.5 }}
                      aria-label={t('allergenStatusMeaningsTitle', 'What do these statuses mean?')}>
                      <InfoOutlinedIcon fontSize="small"
                        sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item
                xs={4}
                sm={1}
                sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton size="small"
                  onClick={() => toggleExpanded(index)}
                  aria-label={isExpanded ? t('collapse', 'Collapse') : t('expand', 'Expand')}>
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small"
                  onClick={() => removeRow(index)}
                  disabled={disabled}
                  aria-label={t('remove', 'Remove')}>
                  <DeleteOutlineIcon fontSize="small"
                    color='error' />
                </IconButton>
              </Grid>
            </Grid>

            <Collapse in={isExpanded}>
              <Grid container
                spacing={3}
                sx={{ mt: 0, pt: 0, pb: 0, pl: '12px', pr: '12px' }}>
                <Grid item
                  xs={12}
                  sm={4}>
                  <TextField
                    label={t('allergenVariantCode', 'Variant Code')}
                    value={row.allergenVariantCode ?? ''}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={4}>
                  <TextField
                    label={t('allergenVariantName', 'Variant Name')}
                    value={row.allergenVariantName ?? ''}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={4}>
                  <TextField
                    label={t('allergenDerivativeCode', 'Derivative Code')}
                    value={row.allergenDerivativeCode ?? ''}
                    onChange={(e) => updateRow(index, { allergenDerivativeCode: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('concentration', 'Concentration')}
                    type="number"
                    value={row.concentration ?? ''}
                    onChange={(e) => updateRow(index, { concentration: e.target.value === '' ? undefined : Number(e.target.value) })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  {referenceOptions ? (
                    <Autocomplete<CodeNameOption>
                      options={referenceOptions.concentrationUoms}
                      getOptionLabel={(option) => option.name || option.code}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      value={referenceOptions.concentrationUoms.find((u) => u.code === row.concentrationUom) ?? null}
                      onChange={(_e, newValue) => updateRow(index, { concentrationUom: newValue?.code ?? '' })}
                      disabled={disabled}
                      size="small"
                      renderInput={(params) => <TextField {...params} 
                        label={t('concentrationUom', 'Concentration Unit')} />}
                    />
                  ) : (
                    <TextField
                      label={t('concentrationUom', 'Concentration Unit')}
                      value={row.concentrationUom ?? ''}
                      onChange={(e) => updateRow(index, { concentrationUom: e.target.value })}
                      size="small"
                      disabled={disabled}
                      fullWidth
                    />
                  )}
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  {referenceOptions ? (
                    <Autocomplete<string>
                      options={referenceOptions.sourceTypes}
                      getOptionLabel={(option) => getEnumLabel(t, ASSESSMENT_SOURCE_TYPE_LABEL_KEYS, option)}
                      value={row.sourceType || null}
                      onChange={(_e, newValue) => updateRow(index, { sourceType: newValue ?? '' })}
                      disabled={disabled}
                      size="small"
                      renderInput={(params) => <TextField {...params} 
                        label={t('sourceType', 'Source Type')} />}
                    />
                  ) : (
                    <TextField
                      label={t('sourceType', 'Source Type')}
                      value={row.sourceType ?? ''}
                      onChange={(e) => updateRow(index, { sourceType: e.target.value })}
                      size="small"
                      disabled={disabled}
                      fullWidth
                    />
                  )}
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <TextField
                    label={t('sourceReference', 'Source Reference')}
                    value={row.sourceReference ?? ''}
                    onChange={(e) => updateRow(index, { sourceReference: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <FormControl fullWidth
                    size="small">
                    <InputLabel shrink>{t('supplierConfirmed', 'Supplier Confirmed')}</InputLabel>
                    <Select
                      value={row.supplierConfirmed ?? ''}
                      onChange={(e: SelectChangeEvent) => updateRow(index, { supplierConfirmed: e.target.value })}
                      displayEmpty
                      size="small"
                      disabled={disabled}
                      fullWidth
                      label={t('supplierConfirmed', 'Supplier Confirmed')}
                      notched
                    >
                      <MenuItem value=""><em>{t('none', 'None')}</em></MenuItem>
                      <MenuItem value="TRUE">{t('yes', 'Yes')}</MenuItem>
                      <MenuItem value="FALSE">{t('no', 'No')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <DatePicker
                    label={t('effectiveFrom', 'Effective From')}
                    value={toDateValue(row.effectiveFrom)}
                    onChange={(newValue) => updateRow(index, { effectiveFrom: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : undefined })}
                    disabled={disabled}
                    maxDate={toDateValue(row.effectiveTo) ?? undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true }, field: { clearable: true } }}
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <DatePicker
                    label={t('effectiveTo', 'Effective To')}
                    value={toDateValue(row.effectiveTo)}
                    onChange={(newValue) => updateRow(index, { effectiveTo: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : undefined })}
                    disabled={disabled}
                    minDate={toDateValue(row.effectiveFrom) ?? undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true }, field: { clearable: true } }}
                  />
                </Grid>
                <Grid item
                  xs={12}>
                  <TextField
                    label={t('notes', 'Notes')}
                    value={row.notes ?? ''}
                    onChange={(e) => updateRow(index, { notes: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                    multiline
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default AllergensTab;
